import {
  FilterDefinition,
  FilterState,
  FilterStateApi,
  FiltersValues,
  KeyValueListItemType,
  KeyValuesFilterValue,
  KeyValuesFilterValues
} from '../../../../../types'

// keyvalue filter structure
// { key: [{ fieldID: key, values: [value, value] }, ...]}
const getKeyValueId = (it: KeyValuesFilterValue): string => it?.fieldID
const getKeyValueObject = (id: string, values: string[]): KeyValuesFilterValue => ({
  fieldID: id,
  values
})
// the above fns act to insulate the rest of the
// code from the structure session-metadata uses { fieldID, values } for api requests
// a more generic { key, values } would be better as a general k/v pattern

// consider consolidating these isXset functions to a single one isFilterSet()
// that takes a filterType and optional id,key,value fields
export const isKeyValueGroupSet = (
  filters: KeyValuesFilterValues,
  key: string,
  filtersList: KeyValueListItemType[]
): boolean =>
  !!filtersList.find(
    (it) => it.id === key && it.values?.every((it) => isKeyValueSet(filters, key, it.value ?? ''))
  )

export const isKeyValueSet = (
  filters: KeyValuesFilterValues,
  key: string,
  value: string
): boolean => !!filters.find((it) => getKeyValueId(it) === key && it.values.includes(value))

const updateFilter = ({
  filters,
  filterKey,
  flag,
  id,
  value
}: { filters: Record<string, KeyValuesFilterValues> } & Pick<
  FilterState,
  'filterKey' | 'flag' | 'id' | 'value'
>): FiltersValues => {
  const idFilter = filterKey ? filters[filterKey].find((it) => getKeyValueId(it) === id) : undefined
  const existingFilterIdRemoved = filterKey
    ? filters[filterKey].filter((it) => getKeyValueId(it) !== id)
    : []
  let updatedFilter: KeyValuesFilterValues = []

  // add
  if (flag) {
    updatedFilter = [
      ...existingFilterIdRemoved,
      getKeyValueObject(id, [...(idFilter?.values || []), ...(value ? [value] : [])])
    ]
    // remove
  } else {
    updatedFilter = existingFilterIdRemoved

    const updatedValues = idFilter?.values.filter((it) => it !== value)
    if (updatedValues?.length) {
      updatedFilter.push(getKeyValueObject(id, updatedValues))
    }
  }

  return { [filterKey as string]: updatedFilter }
}

const getFilterCount = (filters: Record<string, KeyValuesFilterValues>, filterKey: string) =>
  filters[filterKey].reduce((acc, it) => acc + it.values.length, 0)

// URL write
const updateUrl = (
  filters: Record<string, KeyValuesFilterValues>,
  filterName: string,
  updatedUrlSearchParams: URLSearchParams
) =>
  filters[filterName].forEach((pair) =>
    pair.values.forEach((value) =>
      updatedUrlSearchParams.append(`${filterName}-${pair.fieldID}`, encodeURIComponent(value))
    )
  )

// URL read
const readUrl = (filterDef: FilterDefinition, urlParams: URLSearchParams) => {
  const paramFilterKey = `${filterDef.filterKey}-`

  return Array.from(urlParams.keys()) // grab all url params
    .filter((paramKey: string) => paramKey.startsWith(paramFilterKey)) // only keys for this def
    .reduce<string[]>(
      (acc: string[], it: string) => (acc.indexOf(it) !== -1 ? acc : [...acc, it]),
      []
    ) // unique keys
    .map((kvParamKey) =>
      getKeyValueObject(
        kvParamKey.substring(paramFilterKey.length),
        urlParams.getAll(kvParamKey).map(decodeURIComponent)
      )
    )
}

export const stateApi: FilterStateApi = {
  isKeyValueGroupSet,
  isKeyValueSet,
  // @ts-ignore we know this specific filter type is valid
  getFilterCount,
  // @ts-ignore we know this specific filter type is valid
  updateFilter,
  // @ts-ignore we know this specific filter type is valid
  updateUrl,
  readUrl
}
